import services from '../../../core/services';
import { IApplicationsService } from '../../../platform/services/types';
import { AttributeValues } from '../../common/api/attributes';
import { AttributeTypes } from '../../common/constants';
import { validateFormValue } from '../../common/helpers/attributeValidators';
import { DAM_APP_NAME } from '../constants';
import { FileForm } from '../types/fileForm';
import { FilesPictoMapping, ThumbnailProperty, UploadFolder } from '../types/types';

export interface ValidationResult {
  errors: Record<string, Record<string, boolean>>;
  warnings: Record<string, Record<string, boolean>>;
}

/**
 * @param signageItemValues - object of values of the signage item
 * @param form - the form type for the signage item
 * @param [validateInvisibleItems=false] - if should validate invisible attributes
 * @param [breakOnFirstError=false] - if should return as soon as an error is found
 *
 * @returns the errors and warnings triggered by validating the attributes
 */
export const validateAttributes = (
  attributeValues: AttributeValues.ValueObject[],
  formTabs: FileForm.FileFormTab[],
  options?: {
    validateInvisibleItems?: boolean;
    breakOnFirstError?: boolean;
  }
): ValidationResult => {
  const mergedOptions = {
    validateInvisibleItems: false,
    breakOnFirstError: false,
    ...options,
  };

  const problemsResult: ValidationResult = {
    errors: {},
    warnings: {},
  };

  const mergeProblems = (
    attributeId: string,
    problems: {
      errors: Record<string, boolean>;
      warnings: Record<string, boolean>;
    }
  ) => {
    if (Object.keys(problems.errors).length > 0) {
      problemsResult.errors[attributeId] = {
        ...(problemsResult.errors[attributeId] || {}),
        ...problems.errors,
      };
    }

    if (Object.keys(problems.warnings).length > 0) {
      problemsResult.warnings[attributeId] = {
        ...(problemsResult.warnings[attributeId] || {}),
        ...problems.warnings,
      };
    }
  };

  if (!attributeValues || !formTabs) {
    return problemsResult;
  }

  for (const currentTab of formTabs) {
    for (const currentFormAttr of currentTab.attributes) {
      const fileAttr = attributeValues.find(attr => attr.attributeId === currentFormAttr.itemId);

      if (
        !currentFormAttr ||
        !currentFormAttr.options ||
        (!mergedOptions.validateInvisibleItems && !currentFormAttr.options.visible)
      ) {
        // Skip this attribute
        continue;
      }

      const attributeValidationResult = validateFormValue(
        fileAttr?.value,
        currentFormAttr as any
      ) as {
        errors: Record<string, boolean>;
        warnings: Record<string, boolean>;
      };

      mergeProblems(currentFormAttr.itemId, attributeValidationResult);

      if (mergedOptions.breakOnFirstError && Object.keys(problemsResult.errors).length > 0) {
        // Return validation result right away
        return problemsResult;
      }
    }
  }

  return problemsResult;
};

/**
 * Retrieves the picto associated to the file type. Either by mime type
 * or file type
 *
 * @param file - the file whose picto to retrieve
 * @returns the picto associated to this file
 */
export const getFilePicto = (file: {
  description: { type: 'Document' | 'Video' | 'Image' | 'Font' | 'Archive' };
}): string => {
  const filesPictoMapping = services
    .getService<IApplicationsService>('applications')
    ?.getAppParameter<FilesPictoMapping>(DAM_APP_NAME, 'filesPictoMapping', true);

  if (filesPictoMapping instanceof Error || !filesPictoMapping) {
    return '';
  }

  const fileType = file.description.type;

  return filesPictoMapping.fileType[fileType] || filesPictoMapping.default;
};

/**
 * Find the configured color to show, according the the file's attribute values and
 * the color/attribute value mapping in the dam settings
 *
 * @param attributes - the attributes of the file
 * @returns the color of the border to show
 */
export const getFileDynamicBorderColor = (
  attributes: { name?: string; value: string | { itemId: string } | null; type: AttributeTypes }[]
): string | null => {
  const settings = services
    .getService<IApplicationsService>('applications')
    ?.getAppDeepParameter<ThumbnailProperty>(
      DAM_APP_NAME,
      'screensSettings',
      'assets.gallery.thumbnailProperties.borderColor',
      true
    );
  const borderColor = settings instanceof Error ? null : settings;

  if (!borderColor) {
    return null;
  }

  const color = borderColor.default;
  let dynamicMappingColor = null;

  if (borderColor.variant.type === 'attributeValue') {
    const attribute = attributes.find(attr => attr.name === borderColor.variant.attributeFieldName);
    if (attribute) {
      dynamicMappingColor = borderColor.variant.parameters.mappingTable.find(mapping => {
        switch (attribute.type) {
          case AttributeTypes.DATE:
          case AttributeTypes.NUMERIC:
          case AttributeTypes.BOOLEAN:
          case AttributeTypes.STRING:
          case AttributeTypes.LABEL:
            return mapping.value === attribute.value;
          case AttributeTypes.LINKS:
            return (
              typeof attribute.value === 'object' &&
              attribute.value &&
              mapping.value === attribute.value.itemId
            );
          default:
            return false;
        }
      })?.color;
    }
  }

  return dynamicMappingColor || color;
};

export const getUploadRootFolders = (): UploadFolder[] => {
  const folders = services
    .getService<IApplicationsService>('applications')
    ?.getAppDeepParameter<UploadFolder[]>(DAM_APP_NAME, 'screensSettings', 'upload.folders', true);

  return !folders || folders instanceof Error ? [] : folders;
};
